/**
 * The action-dialog pattern (M5 P5e #8): every embedded action opens ONE overlay
 * window — focus-trapped, closed by Escape or an overlay click, never a browser
 * window — and the submit's outcome lands in a small timed toast bar at the top of
 * the page.
 *
 * Author: Mahmoud Ragab Ahmed.
 */

import { useEffect, useRef } from "react";

import { ApiError } from "../api/client";
import { strings } from "../brand/strings";
import { ServerErrorScope, humanField, useUnplacedProblems } from "../lib/serverErrors";
import { RefusalBox } from "../screens/parts";

const FOCUSABLE = "button, [href], input, select, textarea, [tabindex]:not([tabindex='-1'])";

export function ActionDialog({
  open,
  title,
  onClose,
  children,
  wide = false,
  fullSheet = false,
  dismissOnOverlay = true,
  error,
}: {
  readonly open: boolean;
  readonly title: string;
  /**
   * Wave W2: the refusal of THIS dialog's submit. When given, the dialog is its own
   * server-error scope: each field marks its own refusal, and the refusal itself is
   * said INSIDE the dialog, never on the page behind it (Mahmoud's walk: "a return or a
   * cancellation is recorded with its reason" rendered behind the still-open dialog).
   */
  readonly error?: unknown;
  /** A long form (the create-project registration) takes a wider sheet. */
  readonly wide?: boolean;
  /**
   * The inspection day: a working sheet (the conduct checklist) fills a phone's screen
   * and takes a wide, tall window on a desk, its actions pinned to the bottom edge.
   */
  readonly fullSheet?: boolean;
  /** A working sheet is not closed by a stray tap beside it; Escape and Close still do. */
  readonly dismissOnOverlay?: boolean;
  readonly onClose: () => void;
  readonly children: React.ReactNode;
}): React.JSX.Element | null {
  const sheet = useRef<HTMLDivElement>(null);
  // The trap runs ONCE per open (M5 P6 #4): parents recreate `onClose` on every
  // keystroke, so keying the effect on it re-fired the trap — the close button
  // stole focus from the field being typed. The handler reads the latest
  // `onClose` through a ref instead.
  const closeRef = useRef(onClose);
  useEffect(() => {
    closeRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const previous = document.activeElement as HTMLElement | null;
    const node = sheet.current;
    const focusable = node?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? [];
    const firstField = node?.querySelector<HTMLElement>(
      "input:not([type='hidden']), select, textarea",
    );
    (firstField ?? focusable[0] ?? node)?.focus();
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        event.preventDefault();
        closeRef.current();
        return;
      }
      if (event.key === "Tab" && focusable.length > 0) {
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first?.focus();
        }
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      previous?.focus();
    };
  }, [open]);

  if (!open) {
    return null;
  }
  return (
    <div
      className={fullSheet ? "q-dialog__overlay q-dialog__overlay--sheet" : "q-dialog__overlay"}
      data-testid="action-dialog-overlay"
      onClick={dismissOnOverlay ? onClose : undefined}
    >
      <div
        className={
          fullSheet ? "q-dialog q-dialog--sheet" : wide ? "q-dialog q-dialog--wide" : "q-dialog"
        }
        role="dialog"
        aria-modal="true"
        aria-label={title}
        data-testid="action-dialog"
        ref={sheet}
        onClick={(event) => {
          event.stopPropagation();
        }}
      >
        <div className="q-dialog__head">
          <h3 className="q-dialog__title">{title}</h3>
          <button
            type="button"
            className="q-button q-button--quiet"
            aria-label="Close"
            data-testid="action-dialog-close"
            onClick={onClose}
          >
            ×
          </button>
        </div>
        {error === undefined ? (
          children
        ) : (
          <ServerErrorScope error={error}>
            {children}
            <DialogRefusal error={error} />
          </ServerErrorScope>
        )}
      </div>
    </div>
  );
}

/** The refusal inside a dialog, and any place it names that the form has no field for. */
function DialogRefusal({ error }: { readonly error: unknown }): React.JSX.Element | null {
  const unplaced = useUnplacedProblems(error);
  if (!(error instanceof ApiError)) {
    return null;
  }
  return (
    <div data-testid="dialog-refusal">
      <RefusalBox error={error} />
      {unplaced.length > 0 && (
        <ul className="q-note" data-testid="dialog-unplaced">
          {unplaced.map((problem) => (
            <li key={problem.field}>
              {strings.common.unplacedField(
                strings.fieldNames[problem.field.split(".").pop() ?? ""] ??
                  humanField(problem.field),
                problem.message,
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * The timed toast bar: one line at the top of the page, gone on its own. `onDone`
 * clears the parent's state; the parent renders nothing once the message is null.
 */
export function ToastBar({
  message,
  tone = "info",
  onDone,
}: {
  readonly message: string;
  readonly tone?: "info" | "danger";
  readonly onDone: () => void;
}): React.JSX.Element {
  useEffect(() => {
    const timer = setTimeout(onDone, 4000);
    return () => {
      clearTimeout(timer);
    };
  }, [onDone]);
  return (
    <div
      className={`q-toast${tone === "danger" ? " q-toast--danger" : ""}`}
      role="status"
      data-testid="toast-bar"
    >
      {message}
    </div>
  );
}
